/* eslint-disable max-len */
import React, {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {toggleFrameModal, frameType, useFrameService} from '@ey/canvascoreservices';
import {
	useDocumentId,
	useEngagement,
	useEngagementId,
	useBlackListAnalyzerTypes
} from '../../../../../../util/customHooks';
import {labels, GetCardTypeForBody} from '../../../../../../util/utils';
import {guidanceType, HelixComponentKeys} from '../../../../../../util/uiconstants';
import Env from '../../../../../../util/env';
import {expandableGetFormById} from '../../../../../../util/Ares/customHooks';
import {aresTypeEightGuidanceFilter} from '../../../../../Main/Ares/Tasks/TaskView/AresBodies/aresBodyConfig';
import Button from '../../../../../Common/BaseComponentWrappers/MotifButton';
import {
	getAccountBySmartEvidenceSourceId,
	getEngagementIsMest,
	getTaskDocumentByDocumentId
} from '../../../../../../selectors/Ares/selectors';
import {getAccountById} from '../../../../../../actions/Account/accountactions';
import {raiseJSValidationError} from '../../../../../../actions/erroractions';
import {getMultiEntityById} from '../../../../../../actions/MultiEntities/stEntitiesactions';
import HelixV2BodyTab from './HelixV2BodyTab';
import Fetch from '../../../../../../components/Common/Fetch/fetch';
import BodyKnowledgeDescription from '../../BodyKnowledgeDescription';
import MotifTooltip from '../../../../BaseComponentWrappers/MotifTooltip';

export default function HelixV2Body(props) {
	const {
		isAresView,
		showGuidance,
		bodyContent,
		headerId,
		sectionId,
		bodyId,
		getBodiesBySectionId,
		isSnapshotView
	} = props;

	const engagement = useEngagement();
	const documentId = useDocumentId();
	const engagementId = useEngagementId();
	const useBlackListAnalyzerTypesValue = useBlackListAnalyzerTypes();

	const dispatch = useDispatch();

	const formBodyBorder = GetCardTypeForBody(bodyContent, isAresView);

	const formData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
	const {smartEvidenceSourceId} = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
	const hasHelixProjects = useSelector((store) => store.configurations?.features?.hasHelixProjects || false);
	const account = useSelector((state) => getAccountBySmartEvidenceSourceId(state, smartEvidenceSourceId));
	const engagementIsMest = useSelector(getEngagementIsMest);
	const businessUnits = useSelector((state) => state.multiEntity?.stEntityAttributeReadModel?.businessUnits);
	const engagementHelixProjects = useSelector((store) => store.engagementHelixProjects, shallowEqual);
	const helixConnection = engagementHelixProjects.find((x) => x.isPrimary);
	const [guidanceName, setGuidanceName] = useState('');
	const [helixFilters, setHelixFilters] = useState(undefined);

	const frameService = useFrameService();

	const {ActAreaC, BalCompT, BalAreaC, BalComp3P} = HelixComponentKeys;
	const phaseOneGuidanceTypes = [ActAreaC, BalCompT, BalAreaC, BalComp3P];
	const helixGuidance = bodyContent.guidances.find(
		(guidance) => guidance.guidanceTypeId === guidanceType.HelixComponent
	);

	const onClickViewAnalytics = (e) => {
		e.stopPropagation();
		openInDrawer();
	};

	const getGuidanceName = useCallback(() => {
		if (bodyContent.guidances && bodyContent.guidances.length > 0) {
			if (helixGuidance) {
				setGuidanceName(helixGuidance.guidanceName);
				if (engagementHelixProjects) {
					const filters = Env.getHelixChartFilters(
						engagementHelixProjects,
						helixGuidance.guidanceurl,
						smartEvidenceSourceId?.toString(),
						businessUnits
					);

					if (checkGuidancePhaseOne()) {
						filters?.filters.set('isPhaseOne', true);
					} else {
						filters?.filters.set('isPhaseOne', false);
					}

					setHelixFilters(filters);
				}
			}
		}
	}, [
		bodyContent.guidances,
		businessUnits,
		engagementHelixProjects,
		helixGuidance,
		smartEvidenceSourceId,
		checkGuidancePhaseOne
	]);

	const getShowEyHelixSetup = () => {
		let showSetup = false;
		if (!helixFilters) {
			showSetup = true;
		}
		if (engagementIsMest && !businessUnits) {
			showSetup = true;
		}
		return showSetup;
	};

	const openInDrawer = () => {
		let baseUrl = Env.getURL('kronos_web');
		baseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';

		if (window.self === window.top) {
			const _frameUrl = `${baseUrl}standalone/bodyId/${bodyId}/`;
			toggleFrameModal({
				isAresView: true,
				variant: 'slideout',
				frameArgs: {
					canOpenNewWindow: false,
					name: guidanceName,
					frameType: frameType.component,
					src: _frameUrl,
					component: (
						<HelixV2BodyTab
							key={`${helixGuidance.id}-${helixGuidance.guidanceId}-${bodyId}`}
							engagementId={engagementId}
							showEYHelixSetup={getShowEyHelixSetup()}
							isHelixEnabled={hasHelixProjects}
							helixFilters={helixFilters}
							bodyId={bodyId}
						/>
					)
				}
			});
		} else {
			frameService.openFrameFromChild({
				isAresView: true,
				variant: 'slideout',
				frameArgs: {
					canOpenNewWindow: false,
					frameType: frameType.iframe,
					name: guidanceName,
					src: `${baseUrl}standalone/${props.documentId}/header/${headerId}/section/${sectionId}/body/${bodyId}?engagementId=${engagementId}&isdrawer=true`
				}
			});
		}
	};

	const onIconClicked = (e) => {
		e.stopPropagation();
	};

	const checkGuidancePhaseOne = useCallback(() => {
		let found = false;
		phaseOneGuidanceTypes.forEach((type) => {
			if (helixGuidance?.guidanceurl?.includes(type)) {
				found = true;
			}
		});
		return found;
	}, [phaseOneGuidanceTypes, helixGuidance]);

	const getData = useCallback(() => {
		if (!checkGuidancePhaseOne()) {
			dispatch(getAccountById(engagementId, smartEvidenceSourceId)).catch(() =>
				dispatch(raiseJSValidationError(labels.accountNotRelatedToDocumentOnPhaseTwo))
			);
		}
		if (engagementIsMest && (formData.entities.length > 0 || formData.stEntityId)) {
			let stEntityId;
			if (formData.entities.length > 0) {
				stEntityId = formData.entities[0];
			} else {
				stEntityId = formData.stEntityId;
			}
			dispatch(getMultiEntityById(engagementId, stEntityId));
		}
		dispatch(expandableGetFormById(engagement, documentId, undefined, undefined, undefined, undefined, true));
	}, [dispatch, engagementId, smartEvidenceSourceId]);

	useEffect(() => {
		getGuidanceName();
	}, [bodyContent, formData, getGuidanceName]);

	const handleOnClickNavigate = (e) => {
		e.preventDefault();
		window.open(Env.getURL('hercules_web') + '/connections?engagementid=' + engagementId);
	};

	const accountIsMapped = () => {
		return account?.trialBalanceData !== null;
	};

	const getHelixLinkTitleTip = () => {
		let tooltipText = '';
		if (!hasHelixProjects) {
			tooltipText = <span className="TextLabel">{labels.helixNotAvailable}</span>;
		} else if (checkGuidancePhaseOne() && !helixConnection?.isPrimary) {
			tooltipText = (
				<>
					<span className="TextLabel">{labels.oarSetupText.split('{0}')[0]}</span>
					<Button
						variant="text-alt"
						className="helixSetURL"
						onClick={handleOnClickNavigate}
						showLabel
						isEllipsed
						buttonLabel={labels.eyHelixProjects}
						noOfLines={1}
					/>
					<span className="TextLabel">{labels.oarSetupText.split('{0}')[1]}</span>
				</>
			);
		} else if (!checkGuidancePhaseOne() && !accountIsMapped()) {
			tooltipText = <span className="TextLabel">{labels.helixNotMappedToAccount}</span>;
		}
		return tooltipText;
	};

	return (
		<Wrapper className={`SectionBodyWrapper StyledHelixV2Body ${formBodyBorder}`}>
			<BodyKnowledgeDescription
				onIconClicked={onIconClicked}
				disabled={false}
				showGuidance={showGuidance}
				guidanceFilter={aresTypeEightGuidanceFilter}
				isAresView
				isSnapshotView={isSnapshotView}
				bodyModel={bodyContent}
				engagementId={engagementId}
				documentId={documentId}
				headerId={headerId}
				sectionId={sectionId}
				bodyId={bodyId}
				bodyDescriptionText={bodyContent.name}
				getBodiesBySectionId={getBodiesBySectionId}
				showRelateDocumentsIcon={false}
				leftSideButton={
					guidanceName &&
					!useBlackListAnalyzerTypesValue && (
						<section className="view-data-analytic">
							{getHelixLinkTitleTip() !== '' ? (
								<MotifTooltip
									className="HelixV2Tooltip"
									variant="alt"
									trigger={labels.viewDataAnalytic}
									hideCloseButton
								>
									{getHelixLinkTitleTip()}
								</MotifTooltip>
							) : (
								<Fetch fetch={getData}>
									<Button
										variant="text-alt"
										showLabel
										disabled={
											!hasHelixProjects ||
											(checkGuidancePhaseOne() && !helixConnection?.isPrimary) ||
											(!checkGuidancePhaseOne() && !accountIsMapped())
										}
										onClick={(e) => {
											onClickViewAnalytics(e);
										}}
										className="view-data-analytic-button"
										children={labels.viewDataAnalytic}
									/>
								</Fetch>
							)}
						</section>
					)
				}
			/>
		</Wrapper>
	);
}

const Wrapper = styled.section`
	display: block;
	.to-right {
		justify-content: flex-end;
		flex-wrap: nowrap;
		.view-data-analytic {
			margin-right: var(--px-20);
			.view-data-analytic-button {
				font-size: var(--px-12);
				font-weight: 700;
				width: max-content;
				&:disabled,
				&.motif-text-button-disabled {
					background-color: transparent !important;
					border: 0 !important;
					&:hover,
					&:focus {
						background-color: transparent !important;
						border: 0 !important;
					}
				}
			}
			display: flex;
			font-weight: 700;
			font-size: var(--px-14);
			.motif-tooltip-wrapper {
				.motif-tooltip-trigger-wrap {
					cursor: pointer;
				}
			}
		}
	}
`;
